import React, { useCallback, useEffect, useMemo, useState } from "react";
import useStyles from "./style";
import {
  Box,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import moment from "moment";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useDispatch } from "react-redux";

import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import VehicleLeasePurchaseApi from "Services/VehicleLeasePurchase";
import { apiUrl } from "Constants";
import { useNavigate } from "react-router-dom";

const ENTITY_LABEL = "Programme M+";
const FILE_SAFE_ENTITY_LABEL = "ProgrammeMPlus";

const normalizeKey = (value) =>
  (value ?? "")
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toDisplayText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  return value.toString();
};

const toTitleCase = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  return value
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "--");
const formatDateTime = (value) =>
  value ? moment(value).format("DD MMM YYYY, HH:mm") : "--";

const resolveDriverInfo = (row = {}) =>
  row?.driver || row?.driver_rides?.users || row?.driver_rides || {};

const createLeasePurchaseRow = (item = {}, index = 0, offset = 0) => {
  const driverInfo = resolveDriverInfo(item);
  const documents = [
    ...(Array.isArray(item?.documents) ? item.documents : []),
    ...(Array.isArray(item?.driver_documents) ? item.driver_documents : []),
    ...(Array.isArray(item?.driverDocuments) ? item.driverDocuments : []),
    ...(Array.isArray(item?.documents_data) ? item.documents_data : []),
    ...(Array.isArray(item?.files) ? item.files : []),
  ];
  const userId =
    item?.user_id ||
    item?.userId ||
    item?.user?.id ||
    item?.user?._id ||
    item?.driver_rides?.users?.user_id ||
    item?.driver_rides?.users?.userId ||
    item?.driver_rides?.users?._id ||
    item?.driver_rides?.users?.id ||
    item?.driver_id ||
    driverInfo?._id ||
    driverInfo?.id ||
    driverInfo?.driverId ||
    item?.driver?.id ||
    item?.driver?._id ||
    "--";
  const driverName =
    item?.applicant?.name ||
    [driverInfo?.first_name, driverInfo?.last_name].filter(Boolean).join(" ") ||
    driverInfo?.name ||
    "--";
  const photoPath = item?.applicant?.photo_url;

  return {
    id: item?.id || `${userId || item?.driver_id || "lease"}-${index}`,
    sno: offset + index + 1,
    reference: item?.reference || "--",
    applicantCode: item?.applicant?.code || driverInfo?.code || "",
    applicantPhoto: photoPath ? (/^https?:\/\//i.test(photoPath) ? photoPath : `${apiUrl}${photoPath}`) : "",
    existingClient: item?.applicant?.existing_client === true,
    leaseId: item?.id || "--",
    driverId: userId,
    userId,
    driverName,
    driverCode: driverInfo?.code || "--",
    driverEmail: driverInfo?.email || "--",
    driverPhone: driverInfo?.phone || "--",
    driverCity: driverInfo?.city || "--",
    driverCountry: driverInfo?.country || "--",
    driverAddress: driverInfo?.address || item?.driver_rides?.address || "--",
    startDate: item?.start_date || null,
    startDateLabel: formatDate(item?.start_date),
    monthsActive: item?.months_active ?? "--",
    tripAcceptanceRate: item?.trip_acceptance_rate ?? "--",
    cancellationRate: item?.cancellation_rate ?? "--",
    averageTripsPerWeek: item?.average_number_of_trips_per_week ?? "--",
    vehicleType: item?.vehicle_type || "--",
    // Modèle du catalogue M+ (photo, nom, catégorie) quand la demande y est liée.
    vehicleFromCatalog: item?.vehicle?.source === "CATALOG",
    vehicleName: item?.vehicle?.name || "",
    vehicleCategory: item?.vehicle?.category || "",
    vehicleImageUrl: item?.vehicle?.image_url
      ? /^https?:\/\//.test(item.vehicle.image_url)
        ? item.vehicle.image_url
        : `${apiUrl}${item.vehicle.image_url}`
      : "",
    preferredVehicleMake: item?.preferred_vehicle_make || "--",
    preferredVehicleColor: item?.preferred_vehicle_color || "--",
    repaymentPreference: item?.repayment_preference || "--",
    // Durée de remboursement demandée (en mois), calculée par l'API.
    repaymentMonths: item?.repayment_months ?? null,
    nationalIdNumber: item?.national_id_number || "--",
    driversLicenseNumber: item?.drivers_license_number || "--",
    driversLicenseExpirationDate: formatDate(
      item?.drivers_license_expiration_date
    ),
    criminalRecordCertificateReferenceNumber:
      item?.criminal_record_certificate_reference_number || "--",
    guarantorName: item?.guarantor_name || "--",
    guarantorRelationship: item?.guarantor_relationship || "--",
    guarantorPhoneNumber: item?.guarantor_phone_number || "--",
    guarantorIdNumber: item?.guarantor_id_number || "--",
    guarantorAddress: item?.guarantor_address || "--",
    documentCount: documents.length || item?.document_ids?.length || 0,
    status: item?.status || "PENDING",
    reviewedBy: item?.reviewed_by || "--",
    reviewedAt: item?.reviewed_at || null,
    reviewedAtLabel: formatDateTime(item?.reviewed_at),
    createdAtLabel: formatDateTime(item?.created_at),
    updatedAtLabel: formatDateTime(item?.updated_at),
    documents,
    raw: item,
  };
};

const APPROVED_STATUSES = ["APPROVED", "VALIDATED", "ACTIVE", "COMPLETED", "ENABLED"];
const REJECTED_STATUSES = ["BANNED", "REJECTED", "DECLINED", "FAILED", "DISABLED", "CANCELLED"];

// Regroupe les statuts de l'API en trois familles : à étudier, approuvée, refusée.
const getStatusTone = (value) => {
  const status = normalizeKey(value || "PENDING");
  if (APPROVED_STATUSES.includes(status)) return "approved";
  if (REJECTED_STATUSES.includes(status)) return "rejected";
  return "pending";
};

const SEARCH_FIELDS = [
  "reference", "applicantCode", "driverName", "driverCode", "driverEmail", "driverPhone", "driverCity",
  "vehicleType", "vehicleName", "preferredVehicleMake",
  "nationalIdNumber", "driversLicenseNumber", "guarantorName",
];

const ProgrammeMPlus = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [bulkRows, setBulkRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contractsByOwnerId, setContractsByOwnerId] = useState({});

  const fetchLeasePurchases = useCallback(async () => {
    dispatch(showLoader("Loading please wait..."));
    setLoading(true);

    try {
      const params = {
        page: page + 1,
        limit: pageSize,
      };

      const trimmedSearch = searchText.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response =
        await VehicleLeasePurchaseApi.getVehicleLeasePurchaseList(params);

      const payload = response?.data?.data ?? {};
      const leasePurchases = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      const mappedRows = leasePurchases.map((item, index) =>
        createLeasePurchaseRow(item, index, page * pageSize)
      );

      setRows(mappedRows);
      setTotalRecords(payload?.totalRecords ?? mappedRows.length);
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to fetch vehicle lease purchases",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setRows([]);
      setTotalRecords(0);
    } finally {
      dispatch(hideLoader());
      setLoading(false);
    }
  }, [dispatch, page, pageSize, searchText]);

  const fetchAllLeasePurchases = useCallback(async () => {
    try {
      const response =
        await VehicleLeasePurchaseApi.getVehicleLeasePurchaseList({
          page: 1,
          limit: "unlimited",
          search: "",
        });

      const payload = response?.data?.data ?? {};
      const leasePurchases = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      setBulkRows(leasePurchases.map((item, index) => createLeasePurchaseRow(item, index)));
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to fetch all vehicle lease purchases",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setBulkRows([]);
    }
  }, [dispatch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchLeasePurchases();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [fetchLeasePurchases]);

  useEffect(() => {
    fetchAllLeasePurchases();
  }, [fetchAllLeasePurchases]);

  const handleRefresh = useCallback(() => {
    fetchLeasePurchases();
    fetchAllLeasePurchases();
  }, [fetchAllLeasePurchases, fetchLeasePurchases]);

  const handleOpenDetails = useCallback((row) => {
    navigate(`/programme-m-plus/dossiers/${encodeURIComponent(row?.id)}`, { state: { row } });
  }, [navigate]);

  const getExportRows = useCallback(
    (inputRows = []) =>
      inputRows.map((row) => ({
        "S.NO": row?.sno,
        Référence: row?.reference,
        Driver: row?.driverName,
        "Driver Code": row?.driverCode,
        "Driver Email": row?.driverEmail,
        "Driver Phone": row?.driverPhone,
        "Start Date": row?.startDateLabel,
        "Months Active": row?.monthsActive,
        "Vehicle Type": row?.vehicleType,
        "Vehicle Model": row?.vehicleFromCatalog && row?.vehicleName ? row.vehicleName : "--",
        "Vehicle Category": row?.vehicleFromCatalog && row?.vehicleCategory ? row.vehicleCategory : "--",
        "Repayment (months)": row?.repaymentMonths ?? "--",
        "Acceptance Rate": `${toDisplayText(row?.tripAcceptanceRate)}%`,
        "Cancellation Rate": `${toDisplayText(row?.cancellationRate)}%`,
        "Trips / Week": row?.averageTripsPerWeek,
        Documents: row?.documentCount,
        Status: toTitleCase(row?.status),
      })),
    []
  );

  // L'API ne filtre pas par statut : avec un filtre, on part de la liste complète (bulkRows)
  // et on applique recherche + pagination ici.
  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return null;
    const query = searchText.trim().toLowerCase();
    return bulkRows.filter((row) =>
      getStatusTone(row?.status) === statusFilter &&
      (!query || SEARCH_FIELDS.some((field) => String(row?.[field] ?? "").toLowerCase().includes(query)))
    );
  }, [bulkRows, searchText, statusFilter]);

  const displayedRows = useMemo(
    () => (filteredRows ? filteredRows.slice(page * pageSize, (page + 1) * pageSize) : rows),
    [filteredRows, page, pageSize, rows]
  );

  const currentExportRows = useMemo(() => getExportRows(displayedRows), [displayedRows, getExportRows]);
  const allExportRows = useMemo(() => getExportRows(bulkRows), [bulkRows, getExportRows]);

  const handleCopy = useCallback(() => {
    const csv = Papa.unparse(currentExportRows);
    navigator.clipboard.writeText(csv).then(() => {
      dispatch(
        showSnackbar({
          message: "Table copied to clipboard",
          autoHideDuration: 2000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );
    });
  }, [currentExportRows, dispatch]);

  const handleDownloadExcel = useCallback(() => {
    const worksheet = XLSX.utils.json_to_sheet(currentExportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, ENTITY_LABEL);
    XLSX.writeFile(workbook, `${FILE_SAFE_ENTITY_LABEL}List.xlsx`);
  }, [currentExportRows]);

  const handleDownloadCSV = useCallback(() => {
    const csv = Papa.unparse(currentExportRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${FILE_SAFE_ENTITY_LABEL}List.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [currentExportRows]);

  const handleDownloadPDF = useCallback(() => {
    const doc = new jsPDF("landscape");
    const headers = Object.keys(currentExportRows[0] || {
      "S.NO": "",
      Driver: "",
      "Driver Code": "",
      "Driver Email": "",
      "Driver Phone": "",
      "Start Date": "",
      "Months Active": "",
      "Vehicle Type": "",
      "Acceptance Rate": "",
      "Cancellation Rate": "",
      "Trips / Week": "",
      Documents: "",
      Status: "",
    });

    const body = currentExportRows.map((row) => headers.map((header) => row[header]));

    doc.autoTable({
      head: [headers],
      body,
      styles: {
        fontSize: 7,
      },
    });
    doc.save(`${FILE_SAFE_ENTITY_LABEL}List.pdf`);
  }, [currentExportRows]);

  const handleDownloadAll = useCallback(() => {
    const csv = Papa.unparse(allExportRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${FILE_SAFE_ENTITY_LABEL}AllList.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [allExportRows]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "reference",
        headerName: "Référence",
        flex: 0.75,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => <span className={classes.referenceCell}>{params?.row?.reference || "--"}</span>,
      },
      {
        field: "driverName",
        headerName: "Driver",
        flex: 1.3,
        minWidth: 190,
        sortable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const initials = (row?.driverName || "?")
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join("")
            .toUpperCase();

          return (
            <Box className={classes.driverTableCell}>
              {row?.applicantPhoto
                ? <img className={classes.driverAvatar} src={row.applicantPhoto} alt="" />
                : <span className={classes.driverAvatar}>{initials}</span>}
              <Box className={classes.driverCell}>
                <span className={classes.driverPrimary}>
                  {row?.driverName}
                  {row?.existingClient && <span className={classes.existingClientBadge}>Client existant</span>}
                </span>
                <span className={classes.driverSecondary}>
                  {[row?.applicantCode, row?.driverCity, row?.driverCountry].filter((value) => value && value !== "--").join(" · ") || "--"}
                </span>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "vehicleType",
        headerName: "Véhicule demandé",
        flex: 1.25,
        minWidth: 190,
        sortable: false,
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Box className={classes.vehicleCell}>
              {/* Pas de photo du modèle : on n'affiche aucune image. */}
              {row?.vehicleImageUrl ? (
                <img
                  className={classes.vehicleImage}
                  src={row.vehicleImageUrl}
                  alt={row?.vehicleName || ""}
                  style={{ objectFit: "contain" }}
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
              <Box className={classes.vehicleText}>
                <span className={classes.driverPrimary}>
                  {row?.vehicleFromCatalog && row?.vehicleName ? row.vehicleName : toTitleCase(row?.vehicleType)}
                </span>
                <span className={classes.driverSecondary}>
                  {row?.vehicleFromCatalog && row?.vehicleCategory
                    ? row.vehicleCategory
                    : toDisplayText(row?.preferredVehicleMake)}
                </span>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "repaymentMonths",
        headerName: "Durée",
        flex: 0.55,
        minWidth: 82,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => params?.row?.repaymentMonths
          ? `${params.row.repaymentMonths} mois`
          : "--",
      },
      {
        field: "createdAtLabel",
        headerName: "Date de demande",
        flex: 0.85,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => params?.row?.raw?.created_at
          ? moment(params.row.raw.created_at).format("DD/MM/YYYY")
          : "--",
      },
      {
        field: "status",
        headerName: "Statut",
        flex: 0.7,
        minWidth: 105,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const tone = getStatusTone(params?.row?.status);
          const label = tone === "approved" ? "Approuvée" : tone === "rejected" ? "Refusée" : "À étudier";
          const statusColors = tone === "approved"
            ? { backgroundColor: "#d9f2e5", color: "#08734e" }
            : tone === "rejected"
            ? { backgroundColor: "#fde2e0", color: "#b42318" }
            : { backgroundColor: "#fff0d1", color: "#a85c00" };
          return (
            <Chip
              label={label}
              size="small"
              className={`${classes.statusPill} ${classes[`status-${tone}`]}`}
              sx={{ ...statusColors, "& .MuiChip-label": { color: statusColors.color, px: 1 } }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Action",
        flex: 0.7,
        minWidth: 140,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          const rowUserId = row?.userId || row?.driverId;
          return (
            <Box className={classes.rowActions}>
              <Button
                className={classes.examineButton}
                variant="contained"
                size="small"
                onClick={() => handleOpenDetails(row)}
                disabled={!rowUserId || rowUserId === "--"}
              >
                Examiner
              </Button>
            </Box>
          );
        },
      },
    ],
    [classes, handleOpenDetails]
  );

  function CustomToolbar() {
    return (
      <Box
        sx={{
          p: 1,
          pb: 0,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const NoRowsOverlay = () => (
    <Box className={classes.emptyState}>
      <Typography variant="body2">
        No vehicle lease purchase records found for the current filters.
      </Typography>
    </Box>
  );

  const statusSummary = useMemo(() => {
    const summary = { total: bulkRows.length, pending: 0, approved: 0, rejected: 0 };
    bulkRows.forEach((row) => {
      summary[getStatusTone(row?.status)] += 1;
    });
    return summary;
  }, [bulkRows]);

  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.pageHeader}>
          <Box>
            <Typography className={classes.pageTitle}>Demandes M+</Typography>
            <Typography className={classes.pageSubtitle}>
              Consultez et traitez les demandes de financement des chauffeurs.
            </Typography>
          </Box>
          <Button className={classes.refreshButton} variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={handleRefresh}>
            Actualiser
          </Button>
        </Box>

        <Box className={classes.summaryRow}>
          {[
            { label: "Toutes", value: statusSummary.total, tone: "all" },
            { label: "À étudier", value: statusSummary.pending, tone: "pending" },
            { label: "Approuvées", value: statusSummary.approved, tone: "approved" },
            { label: "Refusées", value: statusSummary.rejected, tone: "rejected" },
          ].map((item) => (
            <Box
              key={item.label}
              component="button"
              type="button"
              aria-pressed={statusFilter === item.tone}
              onClick={() => { setStatusFilter(item.tone); setPage(0); }}
              className={`${classes.summaryItem} ${classes[`summary-${item.tone}`]} ${statusFilter === item.tone ? classes.summaryActive : ""}`}
            >
              <Typography className={classes.summaryLabel}>{item.label}</Typography>
              <Typography className={classes.summaryValue}>{item.value}</Typography>
            </Box>
          ))}
          <Box className={classes.searchArea}>
            <TextField
              fullWidth
              size="small"
              placeholder="Rechercher un chauffeur, un véhicule…"
              value={searchText}
              onChange={(event) => { setSearchText(event.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined /></InputAdornment> }}
            />
          </Box>
        </Box>

        <Box className={classes.exportBar}>
          <Typography className={classes.resultsLabel}>Liste des demandes</Typography>
          <Box className={classes.exportActions}>
            <Button startIcon={<ContentCopyOutlined />} onClick={handleCopy}>Copier</Button>
            <Button startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>Excel</Button>
            <Button startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button>
            <Button startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button>
            <Button startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Tout télécharger</Button>
          </Box>
        </Box>

        <Box className={classes.tableWrapper}>
          <DataGrid
            className={classes.dataGridTable}
            rows={displayedRows}
            columns={columns}
            page={page}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            loading={loading}
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={80}
            headerHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={filteredRows ? filteredRows.length : totalRecords}
            filterMode="server"
            onFilterModelChange={(model) => {
              const value = model?.quickFilterValues?.join(" ") || "";
              setSearchText(value);
              setPage(0);
            }}
            density="standard"
            sx={{ width: "100%" }}
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay,
            }}
            getRowId={(row) => row?.id}
          />
        </Box>
      </Box>

    </>
  );
};

export default ProgrammeMPlus;
